'use client';

import NextLink from 'next/link';
import type { ComponentProps } from 'react';
import { forwardRef, useMemo } from 'react';

import resolveHashHref from './resolveHashHref';
import {
  routeToString,
  useLocationSearch,
  useRouter,
  withLocale,
} from './useAppRouter';

type Props = ComponentProps<typeof NextLink>;

function isLocalRoute(value: string) {
  return value.startsWith('/') && !value.startsWith('//');
}

const AppLink = forwardRef<HTMLAnchorElement, Props>(function AppLink(
  { href, locale: requestedLocale, ...props },
  ref,
) {
  const router = useRouter();
  const search = useLocationSearch();
  const localizedHref = useMemo(() => {
    const resolvedHref = resolveHashHref({
      href: routeToString(href),
      pathname: router.asPath,
      search,
    });
    if (!isLocalRoute(resolvedHref)) return resolvedHref;
    const locale =
      requestedLocale === false ? false : requestedLocale || router.locale;
    return withLocale(resolvedHref, locale);
  }, [href, requestedLocale, router.asPath, router.locale, search]);

  return <NextLink {...props} href={localizedHref} ref={ref} />;
});

export default AppLink;
